import { ConfigProps } from "./types/config";

/**
 * Toggle between Stripe DEV and PROD price IDs without touching code.
 * Set `NEXT_PUBLIC_USE_STRIPE_DEV=true` in `.env.local` for testing.
 *
 * NOTE: This is a `NEXT_PUBLIC_*` var so client components can read it too.
 */
const USE_STRIPE_DEV = process.env.NEXT_PUBLIC_USE_STRIPE_DEV === "true";

/** Pick between the dev and prod Stripe price IDs for a given slot. */
const stripeId = (dev: string, prod: string) => (USE_STRIPE_DEV ? dev : prod);

// ---------------------------------------------------------------------------
// STRIPE WIRING - fill these in when the Stripe dashboard is ready.
//
// Everything in this block is data the user edits; UI is in
// components/landing/Pricing.tsx and should not need changes.
// ---------------------------------------------------------------------------

const STRIPE_PORTAL_CONFIG_ID = stripeId(
  // DEV portal config
  "xxxxxxx",
  // PROD portal config
  "bpc_1TrOY0IUM8Gh4ohWLjNrhEVt"
);

const PLAN_PRO = {
  name: "Pro",
  description: "Full Instagram tracking for one account",
  price: 14.99,
  weeklyPrice: 4.99,
  yearlyPrice: 64.99,
  priceId: stripeId("REPLACE_WITH_DEV_PRO_MONTHLY", "price_1TrOW3IUM8Gh4ohWi87qEk4l"),
  weeklyPriceId: stripeId("REPLACE_WITH_DEV_PRO_WEEKLY", "price_1TrOVOIUM8Gh4ohWbF2v8I0n"),
  yearlyPriceId: stripeId("REPLACE_WITH_DEV_PRO_ANNUAL", "price_1TrOWkIUM8Gh4ohWCYLQhI0x"),
  includedCredits: 0,
  features: [
    { name: "1 tracked Instagram account" },
    { name: "Weekly follower & following reports" },
    { name: "Daily post tracking (7-day window)" },
    { name: "Hourly story alerts" },
    { name: "Likes & comments on eligible posts" },
    { name: "Real timestamps on every change" },
    { name: "Same full tracking on every billing interval" },
  ],
};

const PLAN_AGENCY = {
  name: "Agency",
  description: "Team tracking at scale",
  price: 39,
  priceId: stripeId("REPLACE_WITH_DEV_AGENCY_MONTHLY", "REPLACE_WITH_PROD_AGENCY_MONTHLY"),
  includedCredits: 0,
  features: [
    { name: "40 tracked accounts" },
    { name: "15-min snapshots" },
    { name: "Team seats & API access" },
    { name: "Priority crawl queue" },
  ],
};

// One-time credit top-ups use a single Stripe price (quantity = credits purchased).
// Each "package" below is just a preset amount offered on the pricing page -
// every package is billed at `baseRate × credits` (no discounts).
const CREDIT_TOPUP_PRICE_ID = stripeId(
  "REPLACE_WITH_DEV_CREDIT_TOPUP",
  "REPLACE_WITH_PROD_CREDIT_TOPUP"
);
const CREDIT_TOPUP_BASE_RATE = 0.25; // USD per credit
const CREDIT_TOPUP_PACKAGES = [
  { credits: 50 },
  { credits: 100 },
  { credits: 200 },
  { credits: 300 },
  { credits: 500 },
  { credits: 1000 },
];

// ---------------------------------------------------------------------------

const config = {
  // REQUIRED
  appName: "Peeqd",
  appDescription:
    "Instagram activity tracker. See who they follow and unfollow, what they like, and how their posts perform - summarized in clean activity reports.",
  // REQUIRED (no https://, not trialing slash at the end, just the naked domain)
  domainName: "peeqd.app",
  stripe: {
    portalConfigurationId: STRIPE_PORTAL_CONFIG_ID,
    // Subscription plans. The Pricing component highlights the plan flagged
    // with `isFeatured: true` as "Most Popular".
    plans: [
      {
        ...PLAN_PRO,
        isFeatured: true,
        mode: "subscription",
        features: PLAN_PRO.features,
      },
      {
        ...PLAN_AGENCY,
        isMostPowerful: true,
        mode: "subscription",
        features: PLAN_AGENCY.features,
      },
    ],
    creditTopups: {
      priceId: CREDIT_TOPUP_PRICE_ID,
      baseRate: CREDIT_TOPUP_BASE_RATE,
      packages: CREDIT_TOPUP_PACKAGES,
    },
  },
  aws: {
    // If you use AWS S3/Cloudfront, put values in here
    bucket: "bucket-name",
    bucketUrl: `https://bucket-name.s3.amazonaws.com/`,
    cdn: "https://cdn-id.cloudfront.net/",
  },
  resend: {
    // REQUIRED - Email 'From' field to be used when sending magic login links
    fromNoReply: `Peeqd <noreply@peeqd.app>`,
    // REQUIRED - Email 'From' field to be used when sending other emails, like abandoned carts, updates etc..
    fromAdmin: `Eugene at peeqd.app <eugene@vivaco.com>`,
    // Email shown to customer if need support. Leave empty if not needed => if empty, set up Crisp above, otherwise you won't be able to offer customer support."
    supportEmail: "eugene@vivaco.com",
    // When someone replies to supportEmail sent by the app, forward it to the email below (otherwise it's lost). If you set supportEmail to empty, this will be ignored.
    forwardRepliesTo: "eugene@vivaco.com",
  },
  // Legacy Mailgun config - keeping for backward compatibility
  colors: {
    // REQUIRED - The DaisyUI theme to use (added to the main layout.js). Leave blank for default (light & dark mode). If you any other theme than light/dark, you need to add it in config.tailwind.js in daisyui.themes.
    theme: "light",
    // REQUIRED - This color will be reflected on the whole app outside of the document (loading bar, Chrome tabs, etc..). By default it takes the primary color from your DaisyUI theme (make sure to update your the theme name after "data-theme=")
    // OR you can just do this to use a custom color: main: "#f37055". HEX only.
    main: "#EF5F7E",
  },
  auth: {
    // REQUIRED - the path to log in users. It's used to protect private routes.
    // This is used in apiClient (/libs/api.js) upon 401 errors from our API
    loginUrl: "/api/auth/signin",
    // REQUIRED - the path to redirect users after successful login. Marketing
    // and app are merged in this project so users land on "/" (the merged
    // homepage which doubles as the app entry point).
    callbackUrl: "/",
  },
  payments: {
    // REQUIRED - the path to redirect users after successful/failed payment.
    // PaymentResultModal is mounted globally in ClientLayout, so any page that
    // has `?payment_success=...` in the URL will render it. "/" is the merged
    // home/app page, so users land there after checkout.
    successUrl: "/?payment_success=true",
    cancelUrl: "/?payment_success=false",
  },
  credits: {
    initialCredits: 0,
  },
} as ConfigProps;

export default config;
